import { Link, Navigate, useParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { MdxProse } from "../components/MdxProse";
import { MarkdownBody } from "../components/MarkdownBody";
import { BlogPostCta } from "../components/BlogPostCta";
import { useLang, useLp } from "../lib/i18n";
import { getPost, isNoindexPostSlug } from "../lib/blog";
import { blogIndexCopy, blogPostChromeCopy } from "../lib/translations/pages/blog";

const DATE_LOCALE: Record<string, string> = {
  en: "en-US",
  pt: "pt-BR",
  es: "es-ES",
};

export function BlogPost() {
  const { slug = "" } = useParams<{ slug: string }>();
  const lang = useLang();
  const lp = useLp();
  const post = getPost(lang, slug);
  const chrome = blogPostChromeCopy[lang];

  // Missing translation in this language → redirect to the blog index for
  // the same language. Avoids dead-ends when a user follows a link to a
  // post that hasn't been translated yet.
  if (!post) {
    return <Navigate to={lp + "/blog"} replace />;
  }

  const { frontmatter } = post;
  const dateFormatter = new Intl.DateTimeFormat(DATE_LOCALE[lang] ?? "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title={frontmatter.title}
        description={frontmatter.description}
        path={`/blog/${slug}`}
        ogImage={frontmatter.ogImage}
        noindex={isNoindexPostSlug(slug)}
        schema={["blogPosting", "breadcrumbList"]}
        schemaOptions={{
          article: {
            headline: frontmatter.title,
            datePublished: frontmatter.date,
            author: frontmatter.author,
            image: frontmatter.ogImage,
          },
          breadcrumbs: [
            { name: blogIndexCopy[lang].heading, url: lp + "/blog" },
            { name: frontmatter.title, url: lp + "/blog/" + slug },
          ],
        }}
      />
      <Header />

      <main className="flex-1">
        <article className="mx-auto max-w-3xl px-6 pt-16 pb-24 sm:pt-24">
          <Link
            to={lp + "/blog"}
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55 hover:text-ink dark:hover:text-paper transition-colors"
          >
            {chrome.back}
          </Link>

          <header className="mt-8 border-b border-margin dark:border-paper/10 pb-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
              <time dateTime={frontmatter.date}>
                {dateFormatter.format(new Date(frontmatter.date))}
              </time>
            </p>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-ink dark:text-paper leading-[1.1]">
              {frontmatter.title}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-ink/75 dark:text-paper/75">
              {frontmatter.description}
            </p>
          </header>

          {/* Hero image — same asset as og:image, so the preview seen on
              social shares matches what the reader sees when they land. */}
          {frontmatter.ogImage && (
            <figure className="mt-10 -mx-2 sm:mx-0">
              <img
                src={frontmatter.ogImage}
                alt={frontmatter.title}
                width={1200}
                height={630}
                loading="eager"
                className="w-full h-auto rounded-2xl border border-ink/10 dark:border-paper/12"
              />
            </figure>
          )}

          <div className="mt-10">
            <MdxProse>
              <MarkdownBody markdown={post.bodyMD} />
            </MdxProse>
          </div>

          <BlogPostCta />
        </article>
      </main>

      <Footer />
    </div>
  );
}
